import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { SERVICES, WHY_CHOOSE, INDUSTRIES, PROCESS, TESTIMONIALS, IMAGES, COMPANY } from "../mock";
import { Eyebrow, Icon, CtaBanner, Reveal } from "../components/shared";

function Hero() {
  const heroImages = [IMAGES.network, IMAGES.security, IMAGES.av, IMAGES.electrical, IMAGES.smart, IMAGES.maintenance];
  const slides = [...heroImages, heroImages[0]]; // clone first frame for seamless loop
  const [active, setActive] = useState(0);
  const [anim, setAnim] = useState(true);

  useEffect(() => {
    const id = setInterval(() => setActive((p) => p + 1), 5500);
    return () => clearInterval(id);
  }, []);

  // Snap back to the real first frame once the cloned frame finishes sliding in
  const handleEnd = () => {
    if (active >= heroImages.length) {
      setAnim(false);
      setActive(0);
    }
  };
  useEffect(() => {
    if (!anim) {
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
      return () => cancelAnimationFrame(r);
    }
  }, [anim]);

  const dotIndex = active % heroImages.length;
  const go = (n) => {
    setAnim(true);
    setActive((p) => (n > 0 ? p + 1 : p <= 0 ? heroImages.length - 1 : p - 1));
  };

  return (
    <section className="relative overflow-hidden">
      <div className="relative min-h-[calc(100vh-140px)] flex items-center">
        {/* Sliding carousel background (right-to-left) */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="flex h-full"
            style={{
              width: `${slides.length * 100}%`,
              transform: `translateX(-${active * (100 / slides.length)}%)`,
              transition: anim ? "transform 1100ms cubic-bezier(0.65, 0, 0.35, 1)" : "none",
            }}
            onTransitionEnd={handleEnd}
          >
            {slides.map((img, i) => (
              <div key={i} className="h-full" style={{ width: `${100 / slides.length}%` }}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545]/90 via-[#0b2545]/60 to-[#0b2545]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b2545]/70 via-transparent to-[#0b2545]/20" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-[1240px] mx-auto px-5 lg:px-8 py-8">
          <div className="max-w-2xl">
            <p className="reveal text-[12px] font-semibold tracking-[0.25em] text-[#7fb3ff] uppercase" style={{ animationDelay: "0.05s" }}>
              Armstrong Technologies
            </p>
            <h1 className="reveal mt-6 font-display font-extrabold text-white text-[52px] sm:text-[68px] lg:text-[84px] hero-heading" style={{ animationDelay: "0.18s" }}>
              Technical Solutions You Can Trust
            </h1>
            <p className="reveal mt-7 text-[18px] lg:text-[20px] text-white/90 max-w-lg leading-relaxed" style={{ animationDelay: "0.32s" }}>
              At Armstrong Technologies, we specialise in providing high-quality technical installations, maintenance, and support services. Our experienced team works with businesses, public & private sector, property managers, and homeowners to design, install, and maintain systems that improve security, connectivity, efficiency, and performance.
            </p>
            <p className="reveal mt-4 text-[15px] text-white/70 max-w-lg leading-relaxed" style={{ animationDelay: "0.42s" }}>
              We pride ourselves on delivering professional service, transparent communication, and solutions tailored to each client's requirements.
            </p>
            <div className="reveal mt-9 flex flex-wrap gap-3" style={{ animationDelay: "0.54s" }}>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[15px] font-semibold px-7 py-4 hover:bg-[#155ed6] transition-colors">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center rounded-full bg-white/10 border border-white/30 text-white text-[15px] font-semibold px-7 py-4 hover:bg-white/20 transition-colors backdrop-blur-sm">
                Contact Us
              </Link>
              <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} data-testid="hero-call-button" className="inline-flex items-center gap-2 rounded-full bg-[#14b8a6] text-white shadow-sm shadow-[#14b8a6]/25 text-[15px] font-semibold px-7 py-4 hover:bg-[#0f9c8d] transition-colors">
                <Phone className="w-4 h-4" /> Call us
              </a>
            </div>
            <div className="reveal mt-10 flex items-center gap-4" style={{ animationDelay: "0.66s" }}>
              <div className="flex items-center gap-2">
                {heroImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setAnim(true); setActive(i); }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === dotIndex ? "w-9 bg-white" : "w-3 bg-white/40 hover:bg-white/70"}`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 ml-2">
                <button
                  onClick={() => go(-1)}
                  className="w-11 h-11 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-[#0b2545] transition-colors backdrop-blur-sm"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => go(1)}
                  className="w-11 h-11 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-[#0b2545] transition-colors backdrop-blur-sm"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className="overflow-hidden rounded-[20px] shadow-xl" y={40}>
            <img src={IMAGES.reliable} alt="Reliable technical services" className="w-full h-[420px] object-cover" />
          </Reveal>
          <Reveal delay={120}>
            <Eyebrow className="mb-5">About Us</Eyebrow>
            <h2 className="font-display font-extrabold text-[#0b2545] text-[38px] lg:text-[48px] hero-heading">
              Delivering Reliable Technical Services
            </h2>
            <p className="mt-6 text-[16px] text-slate-600 leading-relaxed">
              Professional installation, maintenance, and technical support services for businesses and homeowners.
            </p>
            <p className="mt-4 text-[16px] text-slate-600 leading-relaxed">
              From network infrastructure and security systems to electrical, AV, and smart technology installations, we deliver dependable solutions designed to keep your systems running efficiently.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 text-[#0b2545] text-[15px] font-semibold px-6 py-3 hover:bg-white transition-colors">
              More about us <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#eaf2ff]">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <Eyebrow className="mb-5">Our Services</Eyebrow>
            <h2 className="font-display font-extrabold text-[#0b2545] text-[38px] lg:text-[52px] hero-heading">
              Comprehensive Technical Services
            </h2>
            <p className="mt-6 text-[16px] text-slate-600 leading-relaxed">
              7 service lines covering installation, integration, and long-term support across commercial and residential sites.
            </p>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-[#0b2545] text-[15px] font-semibold px-6 py-3 hover:bg-slate-100 transition-colors whitespace-nowrap">
            All services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 80} className="h-full">
              <div className="svc-card group h-full bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-shadow flex flex-col">
                {/* Details next to heading */}
                <div className="p-6 pb-4">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-[#eaf2ff] flex items-center justify-center">
                      <Icon name={s.icon} className="w-5 h-5 text-[#1e73ff]" />
                    </div>
                    <div>
                      <Link to={`/services/${s.slug}`}>
                        <h3 className="font-display font-bold text-[19px] text-[#0b2545] hover:text-[#14b8a6] transition-colors">{s.title}</h3>
                      </Link>
                      <p className="mt-2 text-[14px] text-slate-500 leading-relaxed">{s.short}</p>
                    </div>
                  </div>
                </div>
                {/* Image underneath */}
                <Link to={`/services/${s.slug}`} className="block px-6">
                  <div className="relative h-40 overflow-hidden rounded-xl">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                  </div>
                </Link>
                {/* Request a Quotation */}
                <div className="p-6 pt-4 mt-auto">
                  <Link to="/contact" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[14px] font-semibold px-5 py-3 hover:bg-[#155ed6] transition-colors">
                    Request a Quotation <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <Reveal>
          <p className="text-[12px] font-semibold tracking-[0.25em] text-[#1e73ff] uppercase mb-5">Why Choose Us</p>
          <h2 className="font-display font-extrabold text-[#0b2545] text-[38px] lg:text-[56px] hero-heading max-w-2xl">
            The Difference Is in the Details
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-2">
          {WHY_CHOOSE.map((w, i) => (
            <Reveal key={i} delay={(i % 2) * 90} className="flex gap-5 py-8 border-t border-black/10">
              <div className="shrink-0 w-11 h-11 rounded-full bg-[#1e73ff] flex items-center justify-center">
                <Icon name={w.icon} className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-display font-bold text-[18px] text-[#0b2545]">{w.title}</h3>
                <p className="mt-2 text-[15px] text-slate-500 leading-relaxed">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#eaf2ff]">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <Eyebrow className="mb-5">Industries We Serve</Eyebrow>
            <h2 className="font-display font-extrabold text-[#0b2545] text-[38px] lg:text-[52px] hero-heading">
              Supporting a Wide Range of Sectors
            </h2>
          </div>
          <Link to="/industries" className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-[#0b2545] text-[15px] font-semibold px-6 py-3 hover:bg-slate-100 transition-colors whitespace-nowrap">
            View sectors <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={i} delay={(i % 4) * 70} className="group bg-white rounded-2xl border border-black/5 p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <Icon name={ind.icon} className="w-12 h-12 text-[#1e73ff]" />
              </div>
              <h3 className="font-display font-bold text-[17px] text-[#0b2545]">{ind.title}</h3>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#dfeafe]">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 mb-16">
          <h2 className="font-display font-extrabold text-[#0b2545] text-[40px] lg:text-[56px] hero-heading max-w-lg">
            Simple, Professional, Effective
          </h2>
          <Link to="/process" className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-[#0b2545] text-[15px] font-semibold px-6 py-3 hover:bg-slate-100 transition-colors whitespace-nowrap">
            Our process <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {PROCESS.map((p, i) => (
            <Reveal key={p.no} delay={(i % 3) * 90} className="border-t border-black/15 pt-6">
              <div className="font-display font-extrabold text-[#0b2545] text-[40px] leading-none">{p.no}</div>
              <h3 className="mt-5 font-display font-bold text-[20px] text-[#0b2545]">{p.title}</h3>
              <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{p.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-20 lg:py-28 bg-[#eaf2ff]">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 mb-14">
          <div>
            <Eyebrow className="mb-4">Testimonials</Eyebrow>
            <h2 className="font-display font-extrabold text-[#0b2545] text-[40px] lg:text-[56px] hero-heading">
              What Our Clients Say
            </h2>
          </div>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-[#0b2545] text-[15px] font-semibold px-6 py-3 hover:bg-slate-100 transition-colors whitespace-nowrap">
            All reviews <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 90} className="bg-white rounded-2xl border border-black/5 p-8 shadow-sm">
              <div className="font-display font-extrabold text-[40px] text-slate-200 leading-none">”</div>
              <p className="mt-4 text-[16px] text-slate-700 leading-relaxed">{t.quote}</p>
              <p className="mt-8 text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500">{t.author}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <div className="group relative z-20 border-b border-black/10 bg-[#eaf2ff] py-3.5 overflow-hidden shadow-[0_8px_20px_rgba(11,37,69,0.06)]">
        <div className="flex whitespace-nowrap animate-marquee group-hover:[animation-play-state:paused]">
          {[...SERVICES, ...SERVICES].map((s, i) => (
            <Link key={i} to={`/services/${s.slug}`} className="mx-8 text-[13px] font-semibold tracking-[0.15em] uppercase text-[#0b2545] hover:text-[#1e73ff] transition-colors inline-flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1e73ff]" /> {s.title}
            </Link>
          ))}
        </div>
      </div>
      <Hero />
      <AboutSection />
      <ServicesSection />
      <WhyChoose />
      <IndustriesSection />
      <ProcessSection />
      <Testimonials />
      <CtaBanner />
    </>
  );
}
